import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { FiX, FiFileText, FiArrowRight } from "react-icons/fi";
import { builtInTemplates, BuiltInTemplate } from "../../data/builtInTemplates";
import { apiService } from "../../services/apiService";

// Type-safe icon wrappers
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const FileTextIcon = FiFileText as React.FC<React.SVGProps<SVGSVGElement>>;
const ArrowRightIcon = FiArrowRight as React.FC<React.SVGProps<SVGSVGElement>>;

interface CustomTemplate {
  id: string;
  name: string;
  description: string;
  subject: string;
}

interface ChooseTemplateModalProps {
  onClose: () => void;
  onSelect: (templateId: string, isBuiltIn: boolean) => void;
}

// Category configuration
const categories = [
  { id: "founders", label: "FOUNDERS" },
  { id: "engineering", label: "ENGINEERING MANAGERS" },
  { id: "product", label: "PRODUCT MANAGERS" },
];

/**
 * ChooseTemplateModal - Template selection modal
 * Matches screenshot design exactly:
 * - Header with icon + title + close
 * - Categories with 2-column grid
 * - Template cards with title, description, subject
 * - Full-width cancel button
 */
const ChooseTemplateModal: React.FC<ChooseTemplateModalProps> = ({ onClose, onSelect }) => {
  const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCustomTemplates();
  }, []);

  const loadCustomTemplates = async () => {
    try {
      const response = await apiService.getEmailTemplates();
      if (response.data) {
        setCustomTemplates(response.data);
      }
    } catch (error) {
      console.error("Failed to load custom templates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleTemplateClick = (templateId: string, isBuiltIn: boolean) => {
    setSelectedId(templateId);
    onSelect(templateId, isBuiltIn);
  };

  // Group built-in templates by category
  const templatesByCategory = builtInTemplates.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = [];
    }
    acc[template.category].push(template);
    return acc;
  }, {} as Record<string, BuiltInTemplate[]>);

  return ReactDOM.createPortal(
    <div className="su-modal-overlay" onClick={handleBackdropClick}>
      <div className="su-modal su-template-modal">
        {/* Header */}
        <div className="su-modal-header">
          <div className="su-modal-title">
            <FileTextIcon />
            <span>CHOOSE A TEMPLATE</span>
          </div>
          <button type="button" className="su-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="su-modal-body su-template-modal-body">
          <p className="su-template-subtitle">
            Select a pre-made template to get started quickly. You can customize it after.
          </p>

          {/* Built-in Templates by Category */}
          {categories.map((category) => {
            const templates = templatesByCategory[category.id];
            if (!templates || templates.length === 0) return null;
            
            return (
              <div key={category.id} className="su-template-category">
                <h4 className="su-template-category-label">{category.label}</h4>
                <div className="su-template-grid">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      className={`su-template-card ${selectedId === template.id ? "selected" : ""}`}
                      onClick={() => handleTemplateClick(template.id, true)}
                    >
                      <div className="su-template-card-content">
                        <h5 className="su-template-card-title">{template.name}</h5>
                        <p className="su-template-card-description">{template.description}</p>
                        <span className="su-template-card-subject">
                          Subject: {template.subject}
                        </span>
                      </div>
                      <ArrowRightIcon className="su-template-card-arrow" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Custom Templates Section */}
          {customTemplates.length > 0 && (
            <div className="su-template-category">
              <h4 className="su-template-category-label">MY TEMPLATES</h4>
              <div className="su-template-grid">
                {customTemplates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className={`su-template-card ${selectedId === template.id ? "selected" : ""}`}
                    onClick={() => handleTemplateClick(template.id, false)}
                  >
                    <div className="su-template-card-content">
                      <h5 className="su-template-card-title">{template.name}</h5>
                      <p className="su-template-card-description">
                        {template.description || "No description"}
                      </p>
                      <span className="su-template-card-subject">
                        Subject: {template.subject}
                      </span>
                    </div>
                    <ArrowRightIcon className="su-template-card-arrow" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="su-modal-footer">
          <button type="button" className="su-template-cancel-btn" onClick={onClose}>
            CANCEL
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ChooseTemplateModal;
